<template>
  <div class="flex w-full gap-8 pb-6">
    <div class="w-1/4 space-y-1">
      <h3 class="font-semibold text-dark-neutral-500">
        Inclusion Criteria and limits
      </h3>
      <p class="text-sm text-gray-600 font-light">
        Define criteria for including studies and applying search frameworks.
      </p>
    </div>
    <div class="w-full space-y-4">
      <SectionCard
        class="w-full"
        title="Apply PICO"
        subtitle="Use the PICO framework (Population, Intervention, Comparator, Outcome) to structure your search strategy."
      >
        <template #header-button>
          <PlanLockGuard>
            <Switch
              :disabled="review.isPlanReadonly.value"
              :model-value="
                planInclusionCriteria?.isPicoInclusionCriteriaApplicable
              "
              @update:model-value="togglePicoInclusionCriteria"
            >
              <template #icon>
                <component
                  :is="
                    planInclusionCriteria?.isPicoInclusionCriteriaApplicable
                      ? ArrowsPointingInIcon
                      : ArrowsPointingOutIcon
                  "
                  class="w-3 h-auto"
                  :class="
                    planInclusionCriteria?.isPicoInclusionCriteriaApplicable
                      ? 'text-green-500'
                      : 'text-dark-neutral-100'
                  "
                />
              </template>
            </Switch>
          </PlanLockGuard>
        </template>

        <div
          v-if="planInclusionCriteria?.isPicoInclusionCriteriaApplicable"
          class="space-y-4"
        >
          <div class="space-y-4">
            <div v-for="section in picoSections" :key="section.title">
              <div class="space-y-2">
                <h3 class="text-dark-neutral-500 text-sm font-semibold">
                  {{ section.title }}
                </h3>

                <div class="flex flex-wrap gap-4 items-center">
                  <div
                    v-for="criterion in section.criteria"
                    :key="section.type + '-' + criterion"
                    class="flex items-center text-sm gap-1 px-4 py-2.5 rounded-full text-green-700 bg-green-100 font-semibold leading-none"
                  >
                    <span
                      v-tooltip="criterion.length > 35 ? criterion : ''"
                      class="truncate max-w-[252px]"
                      >{{ criterion }}</span
                    >
                    <PlanLockGuard>
                      <button
                        :disabled="review.isPlanReadonly.value"
                        @click="
                          deletePicoInclusionCriterion(section.type, criterion)
                        "
                      >
                        <XMarkIcon class="w-4 h-4" />
                      </button>
                    </PlanLockGuard>
                  </div>

                  <PlanLockGuard>
                    <FloatingTextInput
                      :disabled="review.isPlanReadonly.value"
                      @save="addPicoInclusionCriterion(section.type, $event)"
                    >
                      <template #activator="{ show }">
                        <button
                          class="flex items-center gap-1 text-sm border-2 p-2 pr-3 rounded-full text-slate-500 border-slate-300 border-dashed font-semibold"
                          :disabled="review.isPlanReadonly.value"
                          @click.stop="show"
                        >
                          <PlusIcon class="w-4 h-4" /> Add criterion
                        </button>
                      </template>
                    </FloatingTextInput>
                  </PlanLockGuard>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div v-else class="flex flex-wrap gap-4 items-center">
          <div
            v-for="criterion in inclusionCriteria"
            :key="'patient-criterion-' + criterion"
            class="flex items-center text-sm gap-1 px-4 py-2.5 rounded-full text-green-700 bg-green-100 font-semibold leading-none"
          >
            <span
              v-tooltip="criterion.length > 35 ? criterion : ''"
              class="truncate max-w-[252px]"
              >{{ criterion }}</span
            >
            <PlanLockGuard>
              <button
                :disabled="review.isPlanReadonly.value"
                @click="deleteInclusionCriterion(criterion)"
              >
                <XMarkIcon class="w-4 h-4" />
              </button>
            </PlanLockGuard>
          </div>
          <PlanLockGuard>
            <FloatingTextInput
              :disabled="review.isPlanReadonly.value"
              @save="addInclusionCriterion($event)"
            >
              <template #activator="{ show }">
                <button
                  key=""
                  class="flex items-center gap-1 text-sm border-2 py-1.5 px-3 rounded-full text-slate-500 border-slate-500 border-dashed font-semibold"
                  :disabled="review.isPlanReadonly.value"
                  @click.stop="show"
                >
                  <PlusIcon class="w-4 h-4" /> Add criterion
                </button>
              </template>
            </FloatingTextInput>
          </PlanLockGuard>
        </div>
      </SectionCard>

      <SectionCard class="w-full" title="Level of evidence">
        <template #header-button>
          <PlanLockGuard>
            <Switch
              :disabled="review.isPlanReadonly.value"
              :model-value="planInclusionCriteria?.isEvidenceLevelEnabled"
              @update:model-value="toggleLoe"
            >
              <template #icon>
                <component
                  :is="
                    review.entity.value.plan?.inclusionCriteria
                      .isEvidenceLevelEnabled
                      ? ArrowsPointingInIcon
                      : ArrowsPointingOutIcon
                  "
                  class="w-3 h-auto"
                  :class="
                    review.entity.value.plan?.inclusionCriteria
                      .isEvidenceLevelEnabled
                      ? 'text-green-500'
                      : 'text-dark-neutral-100'
                  "
                />
              </template>
            </Switch>
          </PlanLockGuard>
        </template>

        <div
          :class="{
            'opacity-40 cursor-not-allowed pointer-events-none':
              !planInclusionCriteria?.isEvidenceLevelEnabled,
          }"
        >
          <PlanLockGuard>
            <Pyramid
              v-model="loe"
              :disabled="review.isPlanReadonly.value"
              :segments="[
                {
                  level: 60,
                  label: 'Evidence-based clinical guidelines',
                  color: '#06b6d4',
                  id: 'a',
                },
                {
                  level: 70,
                  label: 'Systematic reviews and meta-analyses',
                  color: '#84cc16',
                  id: 'b',
                },
                {
                  level: 80,
                  label: 'Randomized controlled trials',
                  color: '#eab308',
                  id: 'c',
                },
                {
                  level: 90,
                  label: 'Observational studies and cohort studies',
                  color: '#fb923c',
                  id: 'd',
                },
                {
                  level: 100,
                  label:
                    'Case studies, case series, mechanistic reasoning, narrative sources (reviews, textbooks, expert opinion)',
                  color: '#f472b6',
                  id: 'e',
                },
              ]"
              @update:model-value="
                (val) => updateInclusionCriteriaEvidenceLevel(val!)
              "
            />
          </PlanLockGuard>
        </div>
      </SectionCard>

      <SectionCard class="w-full" title="Time limits">
        <template #header-button>
          <PlanLockGuard>
            <Switch
              :disabled="review.isPlanReadonly.value"
              :model-value="planInclusionCriteria?.isTimeLimitEnabled"
              @update:model-value="toggleTimeLimit"
            >
              <template #icon>
                <component
                  :is="
                    review.entity.value.plan?.inclusionCriteria
                      .isTimeLimitEnabled
                      ? ArrowsPointingInIcon
                      : ArrowsPointingOutIcon
                  "
                  class="w-3 h-auto"
                  :class="
                    review.entity.value.plan?.inclusionCriteria
                      .isTimeLimitEnabled
                      ? 'text-green-500'
                      : 'text-dark-neutral-100'
                  "
                />
              </template>
            </Switch>
          </PlanLockGuard>
        </template>

        <div class="flex flex-col gap-4">
          <div
            class="flex items-center gap-6"
            :class="{
              'opacity-40 cursor-not-allowed pointer-events-none':
                !planInclusionCriteria?.isTimeLimitEnabled,
            }"
          >
            <div
              v-for="range in [
                { value: 1, label: '1 year' },
                { value: 5, label: '5 years' },
                { value: 10, label: '10 years' },
                { value: 0, label: 'Custom' },
              ]"
              :key="range.value"
              class="flex items-center"
            >
              <PlanLockGuard>
                <input
                  :id="range.value.toString() + 'year'"
                  v-model="timeLimit"
                  :value="range.value"
                  type="radio"
                  class="relative size-4 appearance-none rounded-full border border-gray-300 bg-white before:absolute before:inset-1 before:rounded-full before:bg-white not-checked:before:hidden checked:border-primary-600 checked:bg-primary-600 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-600 disabled:border-gray-300 disabled:bg-gray-100 disabled:before:bg-gray-400 forced-colors:appearance-auto forced-colors:before:hidden"
                  :disabled="review.isPlanReadonly.value"
                  @change="handleTimeLimitChange"
                />
              </PlanLockGuard>
              <label
                :for="range.value.toString() + 'year'"
                class="ml-2 text-sm font-medium text-gray-900"
              >
                {{ range.label }}
              </label>
            </div>
          </div>
          <PlanLockGuard>
            <DatePicker
              v-model="timeLimitRange"
              class="w-80"
              :class="{
                'opacity-40 cursor-not-allowed pointer-events-none':
                  !planInclusionCriteria?.isTimeLimitEnabled,
              }"
              range
              :disabled="review.isPlanReadonly.value"
              @update:model-value="
                () => {
                  timeLimit = 0
                  handleTimeLimitChange()
                }
              "
            />
          </PlanLockGuard>
        </div>
      </SectionCard>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, watchEffect, computed } from 'vue'
import { injectStrict } from '@app/utils/injectStrict'
import { ReviewKey } from '@app/views/Review/use-review'
import useLoading from '@app/composables/use-loading'
import useSnackbar from '@app/composables/use-snackbar'
import { SnackbarState } from '@app/types'
import { HttpException } from '@core/exceptions/http.exception'
import FloatingTextInput from '@app/components/Global/Inputs/FloatingTextInput.vue'
import { InclusionCriterion } from '@core/domain/models/InclusionCriterion.model'
import SectionCard from '@app/components/Global/SectionCard.vue'
import Switch from '@app/components/Global/Switch.vue'
import PlanLockGuard from '../../PlanLockGuard.vue'
import XMarkIcon from '@app/components/Icons/XMarkIcon.vue'
import PlusIcon from '@app/components/Icons/PlusIcon.vue'
import {
  ArrowsPointingOutIcon,
  ArrowsPointingInIcon,
} from '@heroicons/vue/24/outline'
import Pyramid from '@app/components/Global/Pyramid.vue'
import DatePicker from '@app/components/Global/Inputs/DatePicker.vue'
import { generateTimeLimitRange } from '@app/utils/timeLimits'

const review = injectStrict(ReviewKey)
const { inclusionCriteria: planInclusionCriteria } = review
const loe = ref()

const timeLimit = ref()
const timeLimitRange = ref<[string, string]>(['', ''])

const loading = useLoading()
const snackbar = useSnackbar()

const inclusionCriteria = ref<string[]>([])

const patientCriteria = ref<string[]>([])
const interventionCriteria = ref<string[]>([])
const outcomesCriteria = ref<string[]>([])
const comparisonControlComparator = ref<string[]>([])

watchEffect(() => {
  patientCriteria.value = planInclusionCriteria.value?.patient ?? []
})

watchEffect(() => {
  interventionCriteria.value = planInclusionCriteria.value?.intervention ?? []
})

watchEffect(() => {
  outcomesCriteria.value = planInclusionCriteria.value?.outcomes ?? []
})

watchEffect(() => {
  comparisonControlComparator.value =
    planInclusionCriteria.value?.comparisonControlComparator ?? []
})

watchEffect(() => {
  inclusionCriteria.value = planInclusionCriteria.value?.criteria ?? []
})

watchEffect(() => {
  timeLimit.value = planInclusionCriteria.value?.timeLimit ?? 0
})

watchEffect(() => {
  timeLimitRange.value = [
    planInclusionCriteria.value?.timeLimitFromDate ?? '',
    planInclusionCriteria.value?.timeLimitToDate ?? '',
  ]
})

watchEffect(() => {
  loe.value = planInclusionCriteria.value?.evidenceLevel ?? ''
})
const picoSections = computed(() => [
  {
    title: 'Patient, problem or population',
    criteria: patientCriteria.value,
    type: InclusionCriterion.Patient,
  },
  {
    title: 'Intervention',
    criteria: interventionCriteria.value,
    type: InclusionCriterion.Intervention,
  },
  {
    title: 'Comparison, control or comparator',
    criteria: comparisonControlComparator.value,
    type: InclusionCriterion.ComparisonControlComparator,
  },
  {
    title: 'Outcomes',
    criteria: outcomesCriteria.value,
    type: InclusionCriterion.Outcomes,
  },
])

async function togglePicoInclusionCriteria() {
  loading.start()
  try {
    if (
      review.entity.value.plan?.inclusionCriteria
        .isPicoInclusionCriteriaApplicable
    ) {
      await review.disablePicoInclusionCriteria()
      snackbar.success('Success')
    } else {
      await review.enablePicoInclusionCriteria()
      snackbar.success('Success')
    }
  } catch (e) {
    const error = e as HttpException
    if (
      error.response.data.statusCode >= 400 &&
      error.response.data.statusCode < 500
    ) {
      snackbar.show(SnackbarState.ERROR, error.response.data.message)
    } else {
      snackbar.show(
        SnackbarState.ERROR,
        'An error occured while enabling/disabling PICO inclusion criteria, please try again.',
      )
    }
    throw e
  } finally {
    loading.stop()
  }
}

async function toggleLoe() {
  loading.start()
  try {
    if (review.entity.value.plan?.inclusionCriteria.isEvidenceLevelEnabled) {
      await review.disableEvidenceLevelInclusionCriteria()
      snackbar.success('Success')
    } else {
      await review.enableEvidenceLevelInclusionCriteria()
      snackbar.success('Success')
    }
  } catch (e) {
    const error = e as HttpException
    if (
      error.response.data.statusCode >= 400 &&
      error.response.data.statusCode < 500
    ) {
      snackbar.show(SnackbarState.ERROR, error.response.data.message)
    } else {
      snackbar.show(
        SnackbarState.ERROR,
        'An error occured while enabling/disabling level of evidence inclusion criteria, please try again.',
      )
    }
    throw e
  } finally {
    loading.stop()
  }
}

async function toggleTimeLimit() {
  loading.start()
  try {
    if (review.entity.value.plan?.inclusionCriteria.isTimeLimitEnabled) {
      await review.disableTimeLimtsInclusionCriteria()
      snackbar.success('Success')
    } else {
      await review.enableTimeLimtsInclusionCriteria()

      snackbar.success('Success')
    }
  } catch (e) {
    const error = e as HttpException
    if (
      error.response.data.statusCode >= 400 &&
      error.response.data.statusCode < 500
    ) {
      snackbar.show(SnackbarState.ERROR, error.response.data.message)
    } else {
      snackbar.show(
        SnackbarState.ERROR,
        'An error occured while enabling/disabling time limits inclusion criteria, please try again.',
      )
    }
    throw e
  } finally {
    loading.stop()
  }
}

async function addPicoInclusionCriterion(
  criterionType: InclusionCriterion,
  criterion: string,
) {
  loading.start()
  try {
    await review.addPicoInclusionCriterion({
      criterion: criterion,
      criterionType: criterionType,
    })
    snackbar.success('Success')
  } catch (e) {
    const error = e as HttpException
    if (
      error.response.data.statusCode >= 400 &&
      error.response.data.statusCode < 500
    ) {
      snackbar.show(SnackbarState.ERROR, error.response.data.message)
    } else {
      snackbar.show(
        SnackbarState.ERROR,
        `An error occured while adding criterion to ${{ criterionType }}, please try again.`,
      )
    }
    throw e
  } finally {
    loading.stop()
  }
}

async function deletePicoInclusionCriterion(
  criterionType: InclusionCriterion,
  criterion: string,
) {
  loading.start()
  try {
    await review.deletePicoInclusionCriterion({
      criterion: criterion,
      criterionType: criterionType,
    })
    snackbar.success('Success')
  } catch (e) {
    const error = e as HttpException
    if (
      error.response.data.statusCode >= 400 &&
      error.response.data.statusCode < 500
    ) {
      snackbar.show(SnackbarState.ERROR, error.response.data.message)
    } else {
      snackbar.show(
        SnackbarState.ERROR,
        `An error occured while deleting ${{ criterionType }}, please try again.`,
      )
    }
    throw e
  } finally {
    loading.stop()
  }
}

async function addInclusionCriterion(criterion: string) {
  loading.start()
  try {
    await review.addInclusionCriterion(criterion)
    snackbar.success('Success')
  } catch (e) {
    const error = e as HttpException
    if (
      error.response.data.statusCode >= 400 &&
      error.response.data.statusCode < 500
    ) {
      snackbar.show(SnackbarState.ERROR, error.response.data.message)
    } else {
      snackbar.show(
        SnackbarState.ERROR,
        `An error occured while adding criterion to inclusion criteria, please try again.`,
      )
    }
    throw e
  } finally {
    loading.stop()
  }
}

async function deleteInclusionCriterion(criterion: string) {
  loading.start()
  try {
    await review.deleteInclusionCriterion(criterion)
    snackbar.success('Success')
  } catch (e) {
    const error = e as HttpException
    if (
      error.response.data.statusCode >= 400 &&
      error.response.data.statusCode < 500
    ) {
      snackbar.show(SnackbarState.ERROR, error.response.data.message)
    } else {
      snackbar.show(
        SnackbarState.ERROR,
        `An error occured while deleting inclusion criterion, please try again.`,
      )
    }
    throw e
  } finally {
    loading.stop()
  }
}

async function updateInclusionCriteriaEvidenceLevel(value: string) {
  loading.start()
  try {
    await review.updateInclusionCriteriaEvidenceLevel(value)
    snackbar.success('Success')
  } catch (e) {
    const error = e as HttpException
    if (
      error.response.data.statusCode >= 400 &&
      error.response.data.statusCode < 500
    ) {
      snackbar.show(SnackbarState.ERROR, error.response.data.message)
    } else {
      snackbar.show(
        SnackbarState.ERROR,
        'An error occured while updating level of evidence inclusion criteria, please try again.',
      )
    }
    throw e
  } finally {
    loading.stop()
  }
}

async function handleTimeLimitChange() {
  if (
    timeLimit.value === 0 &&
    (!timeLimitRange.value[0] || !timeLimitRange.value[1])
  ) {
    return
  }
  const range = generateTimeLimitRange(timeLimit.value)
  if (range) {
    timeLimitRange.value = range
  }

  await updateInclusionCriteriaTimeLimit({
    timeLimit: timeLimit.value,
    timeLimitFromDate: timeLimitRange.value[0],
    timeLimitToDate: timeLimitRange.value[1],
  })
}

async function updateInclusionCriteriaTimeLimit({
  timeLimit,
  timeLimitFromDate,
  timeLimitToDate,
}: {
  timeLimit: number
  timeLimitFromDate: string
  timeLimitToDate: string
}) {
  loading.start()
  try {
    await review.updateInclusionCriteriaTimeLimit({
      timeLimit,
      timeLimitFromDate,
      timeLimitToDate,
    })
    snackbar.success('Success')
  } catch (e) {
    const error = e as HttpException
    if (
      error.response.data.statusCode >= 400 &&
      error.response.data.statusCode < 500
    ) {
      snackbar.show(SnackbarState.ERROR, error.response.data.message)
    } else {
      snackbar.show(
        SnackbarState.ERROR,
        'An error occured while updating time limit inclusion criteria, please try again.',
      )
    }
    throw e
  } finally {
    loading.stop()
  }
}
</script>
